import React, {useEffect, useRef, useState, useCallback} from 'react';
import type {SuggestionProps} from '@tiptap/suggestion';
import noop from '@tinkoff/utils/function/noop';
import {Spinner} from '@time-webkit/all/atoms/spinner';
import {PopoverOverlay} from '@time-webkit/all/molecules/popover';
import classNames from 'classnames';
import {BorderRadiusSize, DropdownContainer} from '@time-webkit/all/organisms/dropdown';
import {NavigationList} from '@time-webkit/all/atoms/navigation_list';

import Constants from 'utils/constants';
import {type IEditor} from 'packages/editor';

import styles from './styles.module.css';
import {MentionView, useGetMentions} from './use-get-mentions';
import type {Mention} from './types';
import {RetryView} from './retry-view';

type MentionListProps<M extends Mention> = {
    className?: string;
    getMentions: (query: string) => Promise<M[]>;
    renderMentionOption: (mention: M) => React.ReactNode;
    onSelect: (mention: M, index: number, params?: Record<string, unknown>) => void;
    ignoreEmptyQuery?: boolean;
    editor?: IEditor;
    shouldRequestOnFocus?: boolean;
    hideLoader?: boolean;
    resetOnClose?: boolean;
} & Pick<SuggestionProps<M>, 'query' | 'command' | 'decorationNode'>;

const DEFAULT_MAX_HEIGHT = 362;
const DEFAULT_LOADING_MAX_HEIGHT = 208;
const calcMaxHeight = (mentions: Mention[]) => {
    const padding = 6;
    const rowSize = 36;
    return Math.min((padding * 2) + (mentions.length * rowSize), DEFAULT_MAX_HEIGHT);
};
const SUBMIT_CONTROLS = [
    Constants.KeyCodes.ENTER[0],
    Constants.KeyCodes.TAB[0],
];

export const MentionList = <M extends Mention, >(props: MentionListProps<M>) => {
    const {
        query, getMentions, command, decorationNode,
        renderMentionOption, onSelect,
        ignoreEmptyQuery = true, hideLoader = false,
        shouldRequestOnFocus = false, resetOnClose = false,
    } = props;

    const {view, loading, mentions, retry, request, reset} = useGetMentions<M>(query, getMentions, ignoreEmptyQuery);

    const keys = mentions.map((k) => k.handle);

    useEffect(() => {
        if (!shouldRequestOnFocus) {
            return noop;
        }

        props.editor?.on('focus', request);

        return () => {
            props.editor?.off('focus', request);
        };
    }, [props.editor, request, shouldRequestOnFocus]);

    const shouldShowPopover = mentions.length > 0 || loading;
    const [isOpen, setOpen] = useState(shouldShowPopover);
    const setIsOpen = useCallback((open: boolean) => {
        setOpen(open);

        if (!open && resetOnClose) {
            reset();
        }
    }, [reset, resetOnClose]);
    const lastLoadingHeightRef = useRef(DEFAULT_LOADING_MAX_HEIGHT);

    useEffect(() => {
        if (mentions.length > 0 || loading || view !== MentionView.EMPTY_VIEW) {
            setIsOpen(true);
        }
        lastLoadingHeightRef.current = mentions.length > 0 ? calcMaxHeight(mentions) : DEFAULT_LOADING_MAX_HEIGHT;
    }, [mentions, loading, view, setIsOpen]);

    const handleMentionEnter = (key: string, resultKeyboard = true) => {
        const mention = mentions.find((mention) => mention.handle === key);

        if (!mention) {
            return;
        }

        const index = mentions.indexOf(mention);
        onSelect(mention, index, {resultKeyboard});
        command(mention);
    };

    const wrapWithPopoverOverlay = (children: React.ReactNode) => (
        <PopoverOverlay
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            zIndex={999}
            triggerNode={decorationNode ?? undefined}
            placement='top start'
        >
            {children}
        </PopoverOverlay>
    );

    if (view === MentionView.LOADER_VIEW) {
        return hideLoader ? null : (
            wrapWithPopoverOverlay(
                <DropdownContainer
                    className={classNames(styles.flex, styles.dropdownWrapper, props.className)}
                    borderRadiusSize={BorderRadiusSize.Large}
                    style={{height: lastLoadingHeightRef.current}}
                    data-testid='autocomplete-loader-view'
                >
                    <Spinner />
                </DropdownContainer>,
            )
        );
    }

    if (view === MentionView.RETRY_VIEW) {
        return (
            wrapWithPopoverOverlay(
                <DropdownContainer
                    className={classNames(styles.dropdownWrapper, props.className)}
                    borderRadiusSize={BorderRadiusSize.Large}
                    data-testid='autocomplete-retry-view'
                >
                    <RetryView retry={retry} loading={loading} />
                </DropdownContainer>,
            )
        );
    }

    if (!mentions.length) {
        return null;
    }

    return (
        wrapWithPopoverOverlay(
            <DropdownContainer
                className={classNames(styles.dropdownWrapper, props.className)}
                borderRadiusSize={BorderRadiusSize.Large}
                data-testid='autocomplete-succeed-view'
            >
                <NavigationList
                    keys={keys}
                    onEnter={handleMentionEnter}
                    isolated={true}
                    submitControls={SUBMIT_CONTROLS}
                >
                    {({refs, activeId}) => {
                        return mentions.map((mention) => {
                            return (
                                <div
                                    key={mention.handle}
                                    ref={(ref: HTMLDivElement) => {
                                        refs.current[mention.handle] = ref;
                                    }}
                                    onClick={() => handleMentionEnter(mention.handle, false)}
                                    className={
                                        classNames(
                                            styles.dropdownItem,
                                            {[styles.dropdownItemFocused]: mention.handle === activeId},
                                        )
                                    }
                                >
                                    {renderMentionOption(mention)}
                                </div>
                            );
                        });
                    }}
                </NavigationList>
            </DropdownContainer>,
        )
    );
};
